import FinanceActionModal from '@/components/mirror/FinanceActionModal';
import { financeOriginOperationId, type FinanceActionPrefill } from '@/lib/financeExecutor';
import { financeCurrentRequest } from '../../../../supabase/functions/_shared/financeWriteIntent';
import { financeUuid } from '../../../../supabase/functions/_shared/financeReadIntent';
import type { ActionProposal } from '@/types/mirror';
import type { ModuleCaptureHandler } from '@/lib/capture/types';

const same = (a: unknown, b: unknown) => {
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) || Array.isArray(b)) return false;
  const left = a as Record<string, unknown>, right = b as Record<string, unknown>;
  return Object.keys(left).length === Object.keys(right).length && Object.keys(left).every(key => left[key] === right[key]);
};
const raw = (proposal: ActionProposal) => (proposal.tool === 'propose_finance_action' || proposal.tool === 'propose_finance_log') && typeof proposal.args.raw_text === 'string' ? proposal.args.raw_text : '';
const render: ModuleCaptureHandler<FinanceActionPrefill>['renderModal'] = props => <FinanceActionModal open={props.open} prefill={props.prefill}
  isCurrent={props.isCurrent ?? (() => false)} onClose={props.onClose}
  onCreated={async (receipt, current) => current() && await props.onCreated(receipt) === true} />;

const handler = (tool: 'propose_finance_action' | 'propose_finance_log'): ModuleCaptureHandler<FinanceActionPrefill> => ({
  tool, getRawText: proposal => proposal.tool === tool ? raw(proposal) : '',
  extract: async (rawText, proposal, ctx) => {
    ctx.assertCurrent?.();
    if (proposal.tool !== tool || !financeUuid(ctx.ownerId) || !financeUuid(ctx.sessionId) || !financeUuid(proposal.action_id) || rawText !== ctx.latestUserText) return null;
    const request = financeCurrentRequest(rawText);
    if (!request || tool === 'propose_finance_log' && (request.action !== 'create' || request.entity !== 'transaction')
      || tool === 'propose_finance_action' && !same(request, proposal.args)
      || tool === 'propose_finance_log' && (Object.keys(proposal.args).length !== 1 || proposal.args.raw_text !== rawText)) return null;
    const assert = () => ctx.assertCurrent?.();
    const operation = await financeOriginOperationId(ctx.ownerId, ctx.sessionId, proposal.action_id, 'operation', assert);
    const record = request.action === 'create' ? await financeOriginOperationId(ctx.ownerId, ctx.sessionId, proposal.action_id, 'record', assert) : request.record_id;
    assert();
    // No extractor, money defaults, model values, read DTO snapshot or write.
    return { action: request.action, entity: request.entity, raw_text: request.raw_text, ...(record ? { record_id: record } : {}), operation_id: operation,
      action_id: proposal.action_id, expected_user_id: ctx.ownerId, expected_session_id: ctx.sessionId };
  }, renderModal: render,
});
export const financeActionCaptureHandler = handler('propose_finance_action');
export const financeCaptureHandler = handler('propose_finance_log');
